/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';

import classnames from 'classnames';

import { getCurrentLang, setVisitorLang } from '@/utils/localize';
import { Icon } from '@/components';

/**
 * Languages available from the global switcher. Anyone (logged in or not)
 * can use this to change the UI language; the choice is stored locally in
 * the browser and does not require an account.
 *
 * Rendered as a single icon button, matching the visual style of the
 * neighboring inbox/achievement icon buttons in the header (same size,
 * spacing, hover behavior) rather than a text dropdown, so all header
 * icon actions look consistent. Since only two languages are offered
 * here, a single click just toggles between them.
 *
 * NOTE: intentionally limited to zh_CN / en_US for now. The full language
 * list already exists (see `loadLanguageOptions`) and is used by the
 * per-user "Settings > Interface" and admin "Interface" pages; this
 * component is a lightweight shortcut just for the two most requested
 * languages and can be extended later if needed.
 */
const LANGS = ['zh_CN', 'en_US'];

interface Props {
  navbarStyle?: string;
}

const LangSwitcher: FC<Props> = ({ navbarStyle }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'lang_switcher' });
  const currentLang = getCurrentLang();
  const nextLang = LANGS.find((lang) => lang !== currentLang) || LANGS[0];

  const handleClick = () => {
    setVisitorLang(nextLang);
  };

  return (
    <button
      type="button"
      title={t('title')}
      onClick={handleClick}
      className={classnames(
        'icon-link nav-link d-flex align-items-center justify-content-center p-0 me-3 pointer border-0 bg-transparent',
        {
          'link-light': navbarStyle === 'theme-colored',
          'link-primary': navbarStyle !== 'theme-colored',
        },
      )}>
      <Icon name="translate" className="fs-4" />
    </button>
  );
};

export default memo(LangSwitcher);
